/**
 * Formulas menu: list of saved formulas to choose from
 */
// eslint-disable-next-line no-restricted-imports
import * as React from 'react';
import clsx from 'clsx';

import {ItemClickEvent, Section, SectionItem} from 'common/components/items-menu/types';
import {withStyles} from 'common/util/react';
import {Metric} from 'app_api/projects/metrics/types';
import {getResolvedFormulaEntry} from 'common/util/formulas';
import {ContextPanelData} from 'common/widgets/context-panel/types';
import {FeatureGate, Permission} from 'common/report/constants';
import {userCookieUtils} from 'common/report/util/user-identity-from-cookie';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {ButtonReact} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';
import {ItemsMenuReact} from 'common/components/items-menu';
import {SkeletonListReact} from 'common/components/skeleton-list';
import {SkeletonListRowSize} from 'common/components/skeleton-list/types';
import {ContextPanelReact} from 'common/widgets/context-panel';
import {useCurrentProject} from 'common/contexts/react/current-project-context';
import {useBehaviors} from 'common/contexts/react/behaviors-context';
import {useMetrics} from 'common/contexts/react/metrics-resource-context';
import {IronContextRegistry} from 'common/report/context-registry';

import {ChildMenuInterface, ChildMenuItemsCache, ContextPanelRendererParams} from '../child-menu';
import {MenuSearchBar} from '../mixins/search-bar';
import {getChildMenuHeight} from '../utils/util';
import css from './index.inline.styl';
import {createContextPanelEventHandlers, generateFormulaContextPanelData} from './context-panel';

interface FormulasMenuState {
  searchFilter: string;
}

interface FormulasMenuProps {
  keydownProxyEl?: HTMLElement;
  onSetKeydownProxyEl?: (el: HTMLElement) => void;
  onRender?: () => void;

  onCreate: (ev?: any) => void;
  onSelect: (item) => void;

  selected?: any;
  open?: boolean;

  // Below attributes read by ChildMenu methods
  eventNames?: string[];
  projectId?: number;
  readonly?: boolean;
  trackName?: string;
  userId?: number;
  workspaceId?: number;
}

export type FormulaMenuItemValue = {
  id?: Metric['id'];
  definition?: string;
};

const RECENT_ITEMS_SUB_KEY = `formula`;

export class FormulasMenu
  extends React.PureComponent<
    FormulasMenuProps & Pick<IronContextRegistry, 'currentProject' | 'metrics' | 'behaviors'>,
    FormulasMenuState
  >
  implements ChildMenuInterface
{
  private childMenuRef = React.createRef<HTMLDivElement>();

  state = {
    searchFilter: ``,
  };

  static contextPanelRenderer = (params: ContextPanelRendererParams) => {
    if (!params.tooltipData) {
      return null;
    }

    return (
      <ContextPanelReact
        {...params.contextPanelProps}
        data={params.tooltipData}
        elref='contextPanel'
        {...createContextPanelEventHandlers(params)}
      />
    );
  };

  private _cache = ChildMenuItemsCache.createCache(
    this.props.userId || userCookieUtils.getUserIdentityFromCookie()?.id,
    Number(this.props.projectId || getProjectId()),
    `formulas-menu`,
    Number(this.props.workspaceId || getWorkspaceId()),
  );

  componentDidUpdate() {
    this.props.onRender?.();
  }

  getRecentFormulas() {
    return this._cache.getRecentItems(RECENT_ITEMS_SUB_KEY, 3);
  }

  addRecentFormula(formula) {
    this._cache.addRecentItem(RECENT_ITEMS_SUB_KEY, formula, formula.id);
  }

  canShare() {
    return this.props.currentProject.get()?.value?.getFeatureGate(FeatureGate.Sharing);
  }

  canWriteMetrics() {
    return this.props.currentProject.get()?.value?.hasPermission(Permission.WriteMetrics);
  }

  handleClickCreate() {
    this.props.onCreate();
  }

  handleClickItem({detail}: ItemClickEvent<FormulaMenuItemValue>) {
    const {item} = detail;

    if (item.value.id) {
      this.addRecentFormula(item.value);
    }

    this.props.onSelect({detail: {item: item.value}});
  }

  getSections(): Array<Section<FormulaMenuItemValue>> {
    /** filters metrics according to the user's permissions and sharing */
    const savedFormulas = this.props.metrics
      .getAllValues()
      .filter((metric) => metric.type === `formula` && metric.can_view);

    if (savedFormulas.length) {
      const recentFormulaMenuItems = this.getRecentFormulas() as FormulaMenuItemValue[];
      const recentFormulas = savedFormulas.filter((formula) =>
        recentFormulaMenuItems.some((item) => item.id === formula.id),
      );

      const showRecents = savedFormulas.length > 6 && recentFormulas.length > 0;

      const getMenuItem = (metric: Metric) => ({
        icon: `formula`,
        label: metric.name,
        value: {
          id: metric.id,
        },
      });

      return [
        /** only disply a recently used section when there are enough items to fill in the dropdown */
        {
          label: `Recents`,
          items: showRecents ? recentFormulas.map(getMenuItem) : [],
        },
        {
          label: showRecents ? `All Formulas` : ``,
          items: savedFormulas.map(getMenuItem),
        },
      ].filter(Boolean);
    } else {
      return [];
    }
  }

  /** appends item for an inline formula using the searchFilter as the definition */
  sectionsFromInput = (searchFilter: string): Array<Section<FormulaMenuItemValue>> => {
    const label = `"${searchFilter}"`;
    return [
      {
        items: [
          {
            label,
            value: {
              definition: searchFilter,
            },
            itemFromInput: true,
          },
        ],
      },
    ];
  };

  canOpenMenu() {
    return !this.props.readonly;
  }

  shouldCloseOnSelect() {
    return true;
  }

  selectedLabel() {
    const selected = this.props.selected;

    if (selected?.id) {
      return (
        getResolvedFormulaEntry({
          metricsResource: this.props.metrics,
          behaviorsResource: this.props.behaviors,
          formulaEntry: {event: selected},
        })?.event.name || `Loading...`
      );
    }

    return `Select Formula`;
  }

  getSelectedHoverTooltipData() {
    return this.getTooltipData(this.props.selected?.id);
  }

  getTooltipData(id: number): ContextPanelData {
    return generateFormulaContextPanelData({
      id,
      metricsContext: this.props.metrics,
      canWriteMetrics: this.canWriteMetrics(),
      canShare: this.canShare(),
    });
  }

  render() {
    const isLoading = this.props.metrics.isLoadingAll();

    const contextPanelEventHandlers = createContextPanelEventHandlers({
      availableContexts: {
        metrics: this.props.metrics,
      },
    });

    return (
      <div
        ref={this.childMenuRef}
        className={clsx(`mp-formulas-menu`, `mp-child-menu`)}
        style={{height: getChildMenuHeight()}}
      >
        {this.props.open && (
          <>
            <MenuSearchBar
              ref={(el) => this.props.onSetKeydownProxyEl(el)}
              value={this.state.searchFilter}
              handleSearchInput={(value) => this.setState({searchFilter: value})}
              placeholder='Search...'
            >
              <ButtonReact
                className='create-custom-button'
                theme={ButtonTheme.Tertiary}
                icon={`plus`}
                onClick={() => this.handleClickCreate()}
              >
                Create
              </ButtonReact>
            </MenuSearchBar>
            {isLoading ? (
              <SkeletonListReact
                numRows={6}
                rowSize={SkeletonListRowSize.Large}
                className='mp-formulas-menu-skeleton-list'
              />
            ) : (
              <ItemsMenuReact
                className='mp-formulas-menu-items-menu'
                elref={`mainSelectionMenu`}
                emptyMessage={`No items found`}
                multiple={false}
                searchFilter={this.state.searchFilter}
                disableSearchSort
                disableSearchFilterMatchingParts
                disableFirstItemPadding
                onClick={(ev) => ev.stopPropagation()}
                onClickItem={(ev) => this.handleClickItem(ev)}
                onHoverIn={contextPanelEventHandlers.onHoverIn}
                onHoverOut={contextPanelEventHandlers.onHoverOut}
                onTooltipContextAction={contextPanelEventHandlers.onTooltipContextAction}
                onTooltipContextEditDescription={contextPanelEventHandlers.onTooltipContextEditDescription}
                sections={this.getSections()}
                keydownProxyEl={this.props.keydownProxyEl}
                contextPanelTransform={(item: SectionItem<FormulaMenuItemValue>) => {
                  return item.value.id ? this.getTooltipData(item.value.id) : null;
                }}
                contextPanelPositionParentElement={this.childMenuRef.current}
                sectionsFromInputFn={this.sectionsFromInput}
              />
            )}
          </>
        )}
      </div>
    );
  }
}

export const FormulasMenuReact = withStyles(
  React.forwardRef<FormulasMenu, FormulasMenuProps>((props, ref) => {
    const currentProject = useCurrentProject();
    const metrics = useMetrics();
    const behaviors = useBehaviors();

    return (
      <FormulasMenu ref={ref} currentProject={currentProject} metrics={metrics} behaviors={behaviors} {...props} />
    );
  }),
  css,
);
